import { createUserWithEmailAndPassword, deleteUser } from 'firebase/auth';
import { auth } from '../firebase/config';
import { doc, setDoc, deleteDoc, getDocs, collection, serverTimestamp, Timestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import { logAdminActivity } from '../firebase/helpers';

const ADMIN_USERS_COLLECTION = 'adminUsers';

/**
 * Create a new admin user
 * @param {string} email - Admin email
 * @param {string} password - Admin password
 * @param {string} createdBy - ID of admin creating this user
 * @param {string} createdByEmail - Email of admin creating this user
 * @returns {Promise<Object>} Created admin user info
 */
export const createAdminUser = async (email, password, createdBy, createdByEmail) => {
  try {
    // 1. Create Firebase Auth user
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const newAdminUid = userCredential.user.uid;

    // 2. Create admin document in Firestore
    const adminRef = doc(db, ADMIN_USERS_COLLECTION, newAdminUid);
    await setDoc(adminRef, {
      email: email,
      createdAt: serverTimestamp(),
      createdBy: createdBy,
      createdByEmail: createdByEmail,
      isActive: true
    });

    // 3. Log admin activity
    await logAdminActivity({
      adminId: createdBy,
      adminEmail: createdByEmail,
      action: 'createAdminUser',
      targetUserId: newAdminUid,
      targetUserEmail: email,
      changes: {
        email: email,
        action: 'created'
      },
      description: `Created new admin user: ${email}`
    });

    return {
      success: true,
      uid: newAdminUid,
      email: email,
      message: `Admin user ${email} created successfully`
    };
  } catch (error) {
    console.error('Error creating admin user:', error);
    
    // If Firebase Auth user was created but Firestore doc creation failed,
    // we should delete the Auth user to maintain consistency
    if (error.code === 'auth/email-already-in-use') {
      throw new Error('This email is already registered. Please use a different email.');
    } else if (error.code === 'auth/weak-password') {
      throw new Error('Password is too weak. Please use a stronger password (at least 6 characters).');
    } else if (error.code === 'auth/invalid-email') {
      throw new Error('Invalid email address. Please enter a valid email.');
    } else {
      throw new Error('Failed to create admin user: ' + error.message);
    }
  }
};

/**
 * Delete an admin user
 * @param {string} adminUid - UID of admin to delete
 * @param {string} adminEmail - Email of admin to delete
 * @param {string} deletedBy - ID of admin performing the deletion
 * @param {string} deletedByEmail - Email of admin performing the deletion
 * @returns {Promise<Object>} Deletion result
 */
export const deleteAdminUser = async (adminUid, adminEmail, deletedBy, deletedByEmail) => {
  try {
    // Prevent self-deletion
    if (adminUid === deletedBy) {
      throw new Error('You cannot delete your own admin account.');
    }

    // Delete Firestore document
    // Note: We only delete the Firestore document, not the Firebase Auth user
    // This is because deleting Auth users requires Admin SDK or the user to be signed in
    // Deleting the Firestore document removes admin access, which is sufficient
    // The Auth user account will remain but won't be able to access the admin panel
    const adminRef = doc(db, ADMIN_USERS_COLLECTION, adminUid);
    await deleteDoc(adminRef);

    // Log admin activity
    await logAdminActivity({
      adminId: deletedBy,
      adminEmail: deletedByEmail,
      action: 'deleteAdminUser',
      targetUserId: adminUid,
      targetUserEmail: adminEmail,
      changes: {
        email: adminEmail,
        action: 'deleted'
      },
      description: `Removed admin access for: ${adminEmail}`
    });

    return {
      success: true,
      message: `Admin user ${adminEmail} removed successfully. Note: The Firebase Auth account still exists but admin access has been revoked.`
    };
  } catch (error) {
    console.error('Error deleting admin user:', error);
    throw new Error('Failed to delete admin user: ' + error.message);
  }
};

/**
 * Get all admin users
 * @returns {Promise<Array>} List of admin users
 */
export const getAllAdminUsers = async () => {
  try {
    const adminsRef = collection(db, ADMIN_USERS_COLLECTION);
    const snapshot = await getDocs(adminsRef);
    
    const admins = [];
    snapshot.forEach((doc) => {
      const data = doc.data();
      admins.push({
        uid: doc.id,
        email: data.email || '',
        createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : null,
        createdBy: data.createdBy || null,
        createdByEmail: data.createdByEmail || null,
        isActive: data.isActive !== false, // Default to true if not set
        deletedAt: data.deletedAt?.toDate ? data.deletedAt.toDate() : null
      });
    });

    return admins.sort((a, b) => {
      // Sort by creation date, newest first
      if (a.createdAt && b.createdAt) {
        return b.createdAt - a.createdAt;
      }
      return 0;
    });
  } catch (error) {
    console.error('Error getting admin users:', error);
    throw new Error('Failed to fetch admin users: ' + error.message);
  }
};

